/**
 * Auth Initializer Component
 *
 * 앱 시작 시 백엔드 인증 세션을 초기화하는 컴포넌트
 * - localStorage에서 기존 세션 복원
 * - 세션 변경 리스너 등록
 * - 초기화 완료 전 로딩 표시
 */

import { useEffect } from 'react';
import { Box } from '@mui/material';
import { useAuthStore } from '@/app/stores/authStore';

interface AuthInitializerProps {
  children: React.ReactNode;
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const { initialized, initialize } = useAuthStore();

  useEffect(() => {
    // 앱 시작 시 백엔드 인증 세션 초기화
    initialize();
  }, [initialize]);

  // 초기화 중일 때 로딩 표시
  if (!initialized) {
    return (
      // 가운데 정렬·높이·배경은 index.html 의 `.boot` 한 곳에서 온다 — 여기에 다시 적으면 두 화면이 갈린다
      <Box className="boot" sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* 장식용 배경 glow */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'rgba(147, 197, 253, 0.1)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -120,
            left: -80,
            width: 350,
            height: 350,
            borderRadius: '50%',
            background: 'rgba(37, 99, 235, 0.15)',
            pointerEvents: 'none',
          }}
        />
        {/* index.html 의 정적 화면과 **같은 클래스**를 쓴다 — 모양의 선언은 그 <style> 한 곳뿐이고
            (정적 화면은 JS 가 오기 전에 그려져야 해서 import 할 수 없다), 검사 bootScreen 이 두 곳을 묶는다.
            글자는 두지 않는다 — 이 화면은 세션 중간의 자동 재로드에서도 보인다. */}
        <Box className="boot-spinner" role="progressbar" aria-label="로딩 중" sx={{ zIndex: 1 }}>
          <span className="boot-orbit" />
        </Box>
        <Box
          component="span"
          sx={{
            position: 'absolute',
            bottom: 32,
            display: 'inline-block',
            background: 'rgba(147,197,253,0.15)',
            color: '#93C5FD',
            fontSize: '0.7rem',
            fontWeight: 600,
            px: 1.5,
            py: 0.5,
            borderRadius: 3,
          }}
        >
          VSMR Simulation Platform
        </Box>
      </Box>
    );
  }

  // 초기화 완료 후 자식 컴포넌트 렌더링
  return <>{children}</>;
};

export default AuthInitializer;
